import { useCallback, useEffect, useSyncExternalStore } from 'react';

/**
 * Light, dark, or follow the operating system. The choice is stored under
 * `tabfile.theme` and a boot script in index.html stamps `data-theme` on the
 * root element before first paint, so nobody sees the theme change under them.
 *
 * The default is LIGHT, not the operating system. Tax work is read on whatever
 * device is to hand, and a phone set to dark at night would otherwise open the
 * application in a theme the person never picked for it. "system" remains
 * available; it is chosen, not assumed.
 */
export type ThemeChoice = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'tabfile.theme';

const CHOICES: ThemeChoice[] = ['system', 'light', 'dark'];

let choice: ThemeChoice | undefined;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function readThemeChoice(): ThemeChoice {
  if (choice !== undefined) return choice;
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    choice = CHOICES.includes(raw as ThemeChoice) ? (raw as ThemeChoice) : 'light';
  } catch {
    choice = 'light';
  }
  return choice;
}

function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches === true;
}

export function resolveTheme(value: ThemeChoice): ResolvedTheme {
  if (value === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return value;
}

/** Puts the resolved theme on the root element, where the tokens hang off it. */
export function applyTheme(value: ThemeChoice): void {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', resolveTheme(value));
}

export function setThemeChoice(value: ThemeChoice): void {
  choice = value;
  try {
    localStorage.setItem(THEME_STORAGE_KEY, value);
  } catch {
    // Storage is off. The choice lives for this session only.
  }
  applyTheme(value);
  emit();
}

/** Test helper. Nothing in the app calls it. */
export function resetThemeCache(): void {
  choice = undefined;
  emit();
}

export interface ThemeState {
  choice: ThemeChoice;
  resolved: ResolvedTheme;
  setChoice: (value: ThemeChoice) => void;
}

export function useTheme(): ThemeState {
  const value = useSyncExternalStore(subscribe, readThemeChoice, () => 'light' as ThemeChoice);

  // On "system", follow the operating system while the tab is open.
  useEffect(() => {
    if (value !== 'system' || typeof window === 'undefined' || !window.matchMedia) return;
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      applyTheme('system');
      emit();
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [value]);

  const setChoice = useCallback((next: ThemeChoice) => setThemeChoice(next), []);
  return { choice: value, resolved: resolveTheme(value), setChoice };
}
